import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { countPayrollWarnings } from './payrollWarningHelpers';
import { stdOutlineBtn } from '../adminlayout/designStandard';

// 나머지 경고 펼치기/접기 버튼 (2026-10-01 사용자: "외 6건이 더 있습니다" 인데 볼 방법이 없다 → A안 "더 보기")
const moreBtn = {
  ...stdOutlineBtn, padding: '6px 14px', fontSize: 12,
  display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
};

const LEVEL = {
  danger: { label: '위험', color: '#dc2626', bg: 'rgba(220,38,38,0.08)', border: 'rgba(220,38,38,0.35)' },
  warning: { label: '주의', color: '#d97706', bg: 'rgba(217,119,6,0.10)', border: 'rgba(217,119,6,0.35)' },
  info: { label: '근거', color: '#2563eb', bg: 'rgba(37,99,235,0.08)', border: 'rgba(37,99,235,0.30)' },
};

const cardStyle = {
  background: 'var(--bg-card)',
  borderRadius: 12,
  border: '1px solid var(--border)',
  boxShadow: 'none',
  padding: '14px 16px',
};

function LevelBadge({ severity }) {
  const level = LEVEL[severity] || LEVEL.warning;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 20,
      padding: '0 7px',
      borderRadius: 999,
      background: level.bg,
      border: `1px solid ${level.border}`,
      color: level.color,
      fontSize: 11,
      fontWeight: 900,
      whiteSpace: 'nowrap',
    }}>
      {level.label}
    </span>
  );
}

export default function PayrollWarningsPanel({ warnings = [], isMobile = false }) {
  const [expanded, setExpanded] = useState(false); // 훅은 아래 early return 보다 먼저
  if (!warnings.length) return null;

  const counts = countPayrollWarnings(warnings);
  const limit = isMobile ? 6 : 10;
  const visible = expanded ? warnings : warnings.slice(0, limit);
  const hidden = warnings.length - limit; // 접었을 때 가려지는 건수

  return (
    <div style={{ ...cardStyle, borderColor: 'rgba(217,119,6,0.45)', background: 'linear-gradient(180deg, rgba(217,119,6,0.08), var(--bg-card))' }}>
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 10, flexDirection: isMobile ? 'column' : 'row' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)' }}>급여 확인 필요</div>
          <div style={{ marginTop: 3, fontSize: 12, color: 'var(--text-muted)' }}>
            위험 {counts.danger || 0}건 · 주의 {counts.warning || 0}건 · 근거 {counts.info || 0}건
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#d97706', fontWeight: 800 }}>
          확정 전에 근거를 확인하세요
        </div>
      </div>

      <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
        {visible.map((warning, idx) => (
          <div key={`${warning.code}-${warning.record_id || warning.user_id}-${warning.date || idx}-${idx}`} style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'auto 1fr' : '58px 132px 1fr',
            gap: isMobile ? '6px 8px' : 10,
            alignItems: 'start',
            padding: '8px 10px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.035)',
            border: '1px solid var(--border)',
          }}>
            <LevelBadge severity={warning.severity} />
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-sub)' }}>
              {warning.user_name || '-'}{warning.date ? ` · ${warning.date}` : ''}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.45 }}>
              <span style={{ fontWeight: 900 }}>{warning.title}</span>
              <span style={{ color: 'var(--text-muted)' }}> - {warning.message}</span>
              {warning.detail && (
                <div style={{ marginTop: 3, color: 'var(--text-sub)', fontWeight: 700 }}>
                  {warning.detail}
                </div>
              )}
            </div>
          </div>
        ))}
        {hidden > 0 && (
          <div style={{ textAlign: 'center', paddingTop: 2 }}>
            <button type="button" style={moreBtn} onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
              {expanded
                ? <>접기 <ChevronUp size={14} aria-hidden="true" /></>
                : <>외 {hidden}건 더 보기 <ChevronDown size={14} aria-hidden="true" /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
